/**
 * live/match-presentation.ts — «cosa mostrare» del mini-tabellone, per stato.
 *
 * Funzione **pura**: dallo stato della partita ricava una `BoardPresentation`
 * (fase, cosa disegnare, riga dettagli, enfasi). Il componente `ScorePanel` si
 * limita a renderizzare quello che qui è deciso, così `card` e `broadcast`
 * leggono la stessa mappa e non possono divergere. Nessun colore, nessun CSS,
 * nessuna data «adesso»: il tempo eventualmente serve al chiamante.
 *
 * Nessuna fascia di stato: qualunque informazione sta dentro lo scheletro fisso
 * del tabellone, così **l'altezza non cambia** tra uno stato e l'altro.
 */
import type { MatchState } from './state'

export type BoardPhase =
  | 'before'       // programmata
  | 'starting'     // riscaldamento
  | 'playing'      // in corso
  | 'interrupted'  // pausa / timeout
  | 'ended'        // conclusa
  | 'void'         // rinviata / annullata

/** Enfasi del tabellone: esito positivo, gara senza esito, o nessuna. */
export type BoardEmphasis = 'winner' | 'void' | null

export type BoardDetails =
  | { kind: 'coordinates'; date: string; time: string; venue: string }
  | { kind: 'setHistory' }
  | { kind: 'note'; text: string }
  | { kind: 'none' }

export interface BoardPresentation {
  phase: BoardPhase
  /** Numeri grandi (set corrente; i set vinti a partita conclusa). */
  showBigNumber: boolean
  /** Pastiglia set vinti + pallini timeout. */
  showSets: boolean
  /** Riga in basso: una sola resa per stato. */
  details: BoardDetails
  /**
   * Enfasi visiva **senza righe in più**: `winner` evidenzia la squadra che ha
   * vinto (colore + segno sullo spazio già esistente del servizio), `void`
   * spegne i nomi su rinviata/annullata.
   */
  emphasis: BoardEmphasis
}

export interface BoardPresentationInput {
  coordinates?: { date: string; time: string; venue: string } | null
}

function winnerSide(state: MatchState): 'home' | 'away' | null {
  if (state.winner === 'home') return 'home'
  if (state.winner === 'away') return 'away'
  return null
}

export function presentMatch(state: MatchState, input: BoardPresentationInput = {}): BoardPresentation {
  const coordinates = input.coordinates ?? null
  const hasHistory = state.setHistory.length > 0

  switch (state.status) {
    case 'scheduled':
      return {
        phase: 'before',
        showBigNumber: false,
        showSets: false,
        details: coordinates ? { kind: 'coordinates', ...coordinates } : { kind: 'none' },
        emphasis: null,
      }

    case 'warmup':
      return {
        phase: 'starting',
        showBigNumber: false,
        showSets: false,
        details: coordinates ? { kind: 'coordinates', ...coordinates } : { kind: 'none' },
        emphasis: null,
      }

    case 'pause':
      return {
        phase: 'interrupted',
        showBigNumber: true,
        showSets: true,
        details: { kind: 'note', text: 'Pausa tra i set' },
        emphasis: null,
      }

    case 'timeout_home':
    case 'timeout_away':
      return {
        phase: 'interrupted',
        showBigNumber: true,
        showSets: true,
        details: {
          kind: 'note',
          text: state.status === 'timeout_home'
            ? `Timeout casa · ${state.timeoutsUsed.home}/2`
            : `Timeout ospite · ${state.timeoutsUsed.away}/2`,
        },
        emphasis: null,
      }

    case 'finished':
      return {
        phase: 'ended',
        showBigNumber: true,
        showSets: false,
        details: hasHistory ? { kind: 'setHistory' } : { kind: 'none' },
        emphasis: winnerSide(state) ? 'winner' : 'void',
      }

    case 'postponed':
      return {
        phase: 'void',
        showBigNumber: false,
        showSets: false,
        details: { kind: 'note', text: 'Gara rinviata' },
        emphasis: 'void',
      }

    case 'cancelled':
      return {
        phase: 'void',
        showBigNumber: false,
        showSets: false,
        details: { kind: 'note', text: 'Gara annullata' },
        emphasis: 'void',
      }

    case 'live':
    default:
      return {
        phase: 'playing',
        showBigNumber: true,
        showSets: true,
        details: hasHistory ? { kind: 'setHistory' } : { kind: 'none' },
        emphasis: null,
      }
  }
}

/** Chi ha vinto, per evidenziare la riga giusta. `null` se non c'è un vincitore. */
export function winningSide(state: MatchState): 'home' | 'away' | null {
  return winnerSide(state)
}
